import React, { useState, useEffect, useContext } from 'react';
import { 
  Search, 
  RefreshCw, 
  MapPin, 
  Clock, 
  Activity, 
  AlertCircle,
  Eye,
  Edit,
  Filter,
  Download,
  X
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { TagBlac, TagBlacEstadisticas } from '../../interfaces/TagBlacInterface';
import { getAllTags, getTagsEstadisticas, forzarSincronizacion } from '../../apis/blacApi';
import { tracksolidApi, TagTracksolid } from '../../apis/tracksolidApi';
import { API } from '../../global/global';
import { TagDetailsModal } from './TagDetailsModal';
import { TagEditModal } from './TagEditModal';
import { exportTagsToExcel } from '../../utils/excelExport';

// Tipo unificado para mostrar tags de ambas fuentes
interface TagUnificada extends TagBlac {
  fuente: 'blac' | 'tracksolid';
  imei?: string;
  mc_type?: string;
  tracksolid_id?: number;
}

// Convertir TagTracksolid a TagUnificada
const convertirTracksolidAUnificada = (tag: TagTracksolid): TagUnificada => ({
  id: tag.id,
  name_device: tag.device_name || tag.imei,
  plate: tag.plate || tag.imei,
  vin: tag.imei,
  event: tag.pos_type || undefined,
  longitude: tag.ultima_posicion?.longitude || tag.longitude,
  latitude: tag.ultima_posicion?.latitude || tag.latitude,
  speed: tag.speed || 0,
  odometer: 0,
  address: undefined,
  dateTime: tag.dateTime || undefined,
  estatus: (tag.estatus as 'activo' | 'inactivo') || 'activo',
  ultima_actualizacion: tag.ultima_actualizacion,
  plataforma_id: tag.plataforma_id,
  created_at: '',
  updated_at: '',
  fuente: 'tracksolid',
  imei: tag.imei,
  mc_type: tag.mc_type || undefined,
  tracksolid_id: tag.id,
});

export const Tags: React.FC = () => {
  const authContext = useContext(AuthContext);
  const [tags, setTags] = useState<TagUnificada[]>([]);
  const [allTags, setAllTags] = useState<TagUnificada[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagUnificada[]>([]);
  const [estadisticas, setEstadisticas] = useState<TagBlacEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(50);
  const [syncing, setSyncing] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagUnificada | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [origenFilter, setOrigenFilter] = useState<'todas' | 'principal' | 'subcuentas'>('todas');

  // Verificar si el usuario es de Skyangel (plataforma_id = 4)
  const isSkyangel = authContext.usuario?.plataforma_id === 4;
  const canFilterOrigen = isSkyangel && (authContext.rol === 'Root' || authContext.rol === 'Administrador');

  // Cargar datos iniciales
  useEffect(() => {
    loadTags();
    loadEstadisticas();
  }, []);

  // Filtrar tags cuando cambie el término de búsqueda o el filtro de origen
  useEffect(() => {
    filterTags();
  }, [searchTerm, origenFilter, allTags]);

  // Paginar los tags filtrados
  useEffect(() => {
    paginateFilteredTags();
  }, [filteredTags, currentPage]);

  const loadTags = async () => {
    try {
      setLoading(true);
      
      // Cargar tags de ambas fuentes en paralelo
      const [blacResponse, tracksolidTags] = await Promise.all([
        getAllTags(1, 1000).catch(() => ({ data: [], total: 0 })),
        tracksolidApi.getAllTags().catch(() => []),
      ]);

      // Convertir tags BLAC a formato unificado
      const blacUnificadas: TagUnificada[] = (blacResponse.data || []).map((tag: TagBlac) => ({
        ...tag,
        fuente: 'blac' as const,
      }));

      // Convertir tags Tracksolid a formato unificado
      const tracksolidUnificadas: TagUnificada[] = tracksolidTags.map(convertirTracksolidAUnificada);

      const todas = [...blacUnificadas, ...tracksolidUnificadas];
      setAllTags(todas);
      setTotalPages(Math.ceil(todas.length / 10));
    } catch (error) {
      console.error('Error cargando tags:', error);
      setAllTags([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const [blacStats, tracksolidStats] = await Promise.all([
        getTagsEstadisticas().catch(() => null),
        tracksolidApi.getEstadisticas().catch(() => null),
      ]);

      // Combinar estadísticas
      const blac = blacStats?.estadisticas;
      const ts = tracksolidStats;
      setEstadisticas({
        total_tags: (blac?.total_tags || 0) + (ts?.total_tags || 0),
        tags_activos: (blac?.tags_activos || 0) + (ts?.tags_activos || 0),
        tags_inactivos: (blac?.tags_inactivos || 0),
        tags_con_posicion: (blac?.tags_con_posicion || 0) + (ts?.tags_con_posicion || 0),
        tags_sin_posicion: (blac?.tags_sin_posicion || 0) + (ts?.tags_sin_posicion || 0),
        ultima_sincronizacion: blac?.ultima_sincronizacion || '',
      });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  // Función para filtrar tags localmente
  const filterTags = () => {
    let resultado = allTags;

    // Filtrar por origen (cuenta principal vs subcuentas) - solo Skyangel Root/Admin
    if (canFilterOrigen && origenFilter === 'principal') {
      resultado = resultado.filter(tag => tag.plataforma_id === 4);
    } else if (canFilterOrigen && origenFilter === 'subcuentas') {
      resultado = resultado.filter(tag => tag.plataforma_id !== 4);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      resultado = resultado.filter(tag => {
        return (
          tag.name_device.toLowerCase().includes(searchLower) ||
          tag.plate.toLowerCase().includes(searchLower) ||
          (tag.address && tag.address.toLowerCase().includes(searchLower)) ||
          (tag.vin && tag.vin.toLowerCase().includes(searchLower)) ||
          (tag.event && tag.event.toLowerCase().includes(searchLower)) ||
          tag.estatus.toLowerCase().includes(searchLower) ||
          ((tag as TagUnificada).imei && (tag as TagUnificada).imei!.toLowerCase().includes(searchLower)) ||
          ((tag as TagUnificada).mc_type && (tag as TagUnificada).mc_type!.toLowerCase().includes(searchLower))
        );
      });
    }

    setFilteredTags(resultado);
    setCurrentPage(1);
  };

  // Función para paginar los tags filtrados
  const paginateFilteredTags = () => {
    const itemsPerPage = 10;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTags = filteredTags.slice(startIndex, endIndex);
    
    setTags(paginatedTags);
    setTotalPages(Math.ceil(filteredTags.length / itemsPerPage));
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await Promise.all([
        forzarSincronizacion().catch(e => console.error('Error sync Blac:', e)),
        tracksolidApi.sincronizar().catch(e => console.error('Error sync Tracksolid:', e)),
      ]);
      await loadTags();
      await loadEstadisticas();
    } catch (error) {
      console.error('Error en sincronización:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateTag = async (tagId: number, plataformaId: number) => {
    try {
      const response = await fetch(`${API}/tags/${tagId}/plataforma`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plataforma_id: plataformaId })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la tag');
      }

      // Recargar las tags para mostrar los cambios
      await loadTags();
    } catch (error) {
      throw error;
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // El filtrado se ejecutará automáticamente por el useEffect
  };

  // Helper function para convertir valores a número de manera segura
  const safeNumber = (value: number | string | undefined): number => {
    if (value === undefined || value === null) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    
    // Si es formato YYYYMMDDHHMMSS, convertir a fecha legible
    if (dateTime.length === 14) {
      const year = dateTime.substring(0, 4);
      const month = dateTime.substring(4, 6);
      const day = dateTime.substring(6, 8);
      const hour = dateTime.substring(8, 10);
      const minute = dateTime.substring(10, 12);
      const second = dateTime.substring(12, 14);
      
      return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
    }
    
    // Si es formato ISO, convertir
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  const getStatusColor = (estatus: string) => {
    switch (estatus) {
      case 'activo':
        return 'text-green-400 bg-green-400/10';
      case 'inactivo':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getEventColor = (event?: string) => {
    if (!event) return 'text-gray-400';
    
    switch (event.toLowerCase()) {
      case 'gps':
      case 'posicion':
        return 'text-blue-400';
      case 'stop':
      case 'parada':
        return 'text-red-400';
      case 'movimiento':
        return 'text-green-400';
      default:
        return 'text-yellow-400';
    }
  };

  const styles = `
    .tags-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      margin-bottom: 2rem;
    }

    .page-title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 0.5rem;
    }

    .page-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1.1rem;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      transition: all 0.3s ease;
    }

    .stat-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 122, 255, 0.3);
      transform: translateY(-2px);
    }

    .stat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .stat-title {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
      font-weight: 500;
    }

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      color: #007AFF;
    }

    .controls-section {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
    }

    .search-container {
      position: relative;
      flex: 1;
      max-width: 400px;
    }

    .search-input {
      width: 100%;
      padding: 12px 16px 12px 48px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: #007AFF;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255, 255, 255, 0.5);
    }

    .action-buttons {
      display: flex;
      gap: 1rem;
    }

    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
    }

    .btn-primary {
      background: #007AFF;
      color: white;
    }

    .btn-primary:hover {
      background: #0056CC;
      transform: translateY(-1px);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    .table-container {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      overflow: hidden;
    }

    .table {
      width: 100%;
      border-collapse: collapse;
    }

    .table th {
      background: rgba(255, 255, 255, 0.1);
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .table td {
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.8);
    }

    .table tr:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    .event-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      font-weight: 500;
    }

    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-top: 2rem;
      padding: 1rem;
    }

    .pagination-btn {
      padding: 8px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .pagination-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
    }

    .pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pagination-btn.active {
      background: #007AFF;
      border-color: #007AFF;
    }

    .loading-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 4rem;
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .empty-icon {
      margin: 0 auto 1rem;
      opacity: 0.5;
    }

    .search-results-indicator {
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .page-title {
        font-size: 2rem;
      }

      .controls-section {
        flex-direction: column;
        align-items: stretch;
      }

      .search-container {
        max-width: none;
      }

      .action-buttons {
        justify-content: center;
      }

      .table-container {
        overflow-x: auto;
      }

      .table {
        min-width: 800px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="tags-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <h1 className="page-title">Tags</h1>
            <p className="page-subtitle">
              Gestión y monitoreo de dispositivos de tracking
            </p>
          </div>

          {/* Estadísticas */}
          {estadisticas && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Total Tags</span>
                  <Activity size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.total_tags}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem' }}>
                  {searchTerm ? `${filteredTags.length} mostrados` : 'En el sistema'}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Tags Activos</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                </div>
                <div className="stat-value">{estadisticas.tags_activos}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Con Posición</span>
                  <MapPin size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.tags_con_posicion}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Última Sync</span>
                  <Clock size={20} color="#007AFF" />
                </div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {formatDateTime(estadisticas.ultima_sincronizacion)}
                </div>
              </div>
            </div>
          )}

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar por tag, Nombre o estado..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                  }}
                  title="Limpiar búsqueda"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="action-buttons">
              {canFilterOrigen && (
                <select
                  value={origenFilter}
                  onChange={(e) => setOrigenFilter(e.target.value as 'todas' | 'principal' | 'subcuentas')}
                  style={{
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    minWidth: '180px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <option value="todas" style={{ background: '#1a1a1a' }}>Todas las tags</option>
                  <option value="principal" style={{ background: '#1a1a1a' }}>Cuenta principal</option>
                  <option value="subcuentas" style={{ background: '#1a1a1a' }}>Subcuentas</option>
                </select>
              )}

              <button
                onClick={handleSync}
                disabled={syncing}
                className="btn btn-primary"
              >
                {syncing ? (
                  <RefreshCw className="animate-spin" size={16} />
                ) : (
                  <RefreshCw size={16} />
                )}
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>

              <button
                className="btn btn-secondary"
                onClick={() => exportTagsToExcel(filteredTags)}
              >
                <Download size={16} />
                Exportar
              </button>
            </div>
          </div>

          {/* Indicador de resultados */}
          {searchTerm && (
            <div className="search-results-indicator" style={{
              padding: '1rem 2rem',
              background: 'rgba(0, 122, 255, 0.1)',
              border: '1px solid rgba(0, 122, 255, 0.2)',
              borderRadius: '12px',
              marginBottom: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem'
            }}>
              🔍 Mostrando {filteredTags.length} resultado{filteredTags.length !== 1 ? 's' : ''} para "{searchTerm}"
              {filteredTags.length === 0 && (
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', marginLeft: '0.5rem' }}>
                  - Intenta con otros términos de búsqueda
                </span>
              )}
            </div>
          )}

          {/* Tabla de Tags */}
          <div className="table-container">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner" />
              </div>
            ) : tags.length === 0 ? (
              <div className="empty-state">
                <AlertCircle className="empty-icon" size={48} />
                <h3>
                  {searchTerm ? 'No se encontraron resultados' : 'No se encontraron tags'}
                </h3>
                <p>
                  {searchTerm 
                    ? `No hay tags que coincidan con "${searchTerm}". Intenta con otros términos de búsqueda.`
                    : 'No hay tags registrados en el sistema. Ejecuta una sincronización para obtener datos.'
                  }
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      marginTop: '1rem',
                      padding: '0.5rem 1rem',
                      background: '#007AFF',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Último Evento</th>
                    <th>Posición</th>
                    <th>Última Actualización</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={`${(tag as TagUnificada).fuente}-${tag.id}`}>
                      <td>
                        <div>
                          <div style={{ fontWeight: 500 }}>{tag.name_device}</div>
                          {tag.vin && (
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                              {tag.vin}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <code style={{ 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          {tag.plate}
                        </code>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusColor(tag.estatus)}`}>
                          <div style={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            backgroundColor: tag.estatus === 'activo' ? '#10B981' : '#EF4444' 
                          }} />
                          {tag.estatus}
                        </span>
                      </td>
                      <td>
                        {tag.event ? (
                          <span className={`event-badge ${getEventColor(tag.event)}`}>
                            <Activity size={12} />
                            {tag.event}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>N/A</span>
                        )}
                      </td>
                      <td>
                        {tag.latitude && tag.longitude ? (
                          <div style={{ fontSize: '0.8rem' }}>
                            <div>{safeNumber(tag.latitude).toFixed(6)}</div>
                            <div>{safeNumber(tag.longitude).toFixed(6)}</div>
                          </div>
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Sin posición</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {formatDateTime(tag.ultima_actualizacion)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setSelectedTag(tag)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                          >
                            <Eye size={14} />
                            Ver
                          </button>
                          {isSkyangel && authContext.tienePermiso('tags.editar') && (
                            <button
                              onClick={() => {
                                setSelectedTag(tag);
                                setShowEditModal(true);
                              }}
                              className="btn btn-primary"
                              style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                            >
                              <Edit size={14} />
                              Editar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Anterior
              </button>

              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Página {currentPage} de {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalles */}
      {selectedTag && !showEditModal && (
        <TagDetailsModal
          tag={selectedTag}
          isOpen={!!selectedTag}
          onClose={() => setSelectedTag(null)}
        />
      )}

      {/* Modal de edición */}
      {selectedTag && showEditModal && (
        <TagEditModal
          tag={selectedTag}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTag(null);
          }}
          onSave={handleUpdateTag}
        />
      )}
    </>
  );
};
